import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

export interface ConditionTypeOption {
  key: string;
  value: string;
}


@Injectable({
  providedIn: 'root'
})
export class ConditionTypesService {
  private translate = inject(TranslateService);

  // Valores centralizados de appliesTo
  public readonly APPLIES_TO_VALUES = {
    TRANSFERS: 'transfers',
    CREDITS: 'credits', 
    CALLS: 'calls',
    MOBILE_RATES: 'mobileRates',
    TRAVEL_SIMS: 'travelSims'
  } as const;

  // Mapeo de valores a claves de traducción
  public readonly TRANSLATION_KEYS = {
    [this.APPLIES_TO_VALUES.TRANSFERS]: 'LISTS.PARTNER.TRANSFERS',
    [this.APPLIES_TO_VALUES.CREDITS]: 'LISTS.PARTNER.LOANS',
    [this.APPLIES_TO_VALUES.CALLS]: 'LISTS.PARTNER.INTERNATIONAL_CALLS',
    [this.APPLIES_TO_VALUES.MOBILE_RATES]: 'LISTS.PARTNER.MOBILE_RATES',
    [this.APPLIES_TO_VALUES.TRAVEL_SIMS]: 'LISTS.PARTNER.TRAVELSIMS'
  };

  /**
   * Obtiene todas las opciones de tipos de condición para filtros
   */
  async getConditionTypeOptions(): Promise<ConditionTypeOption[]> {
    const options: ConditionTypeOption[] = [
      { key: '-1', value: await firstValueFrom(this.translate.get('COMMON.ALL')) }
    ];

    for (const [key, translationKey] of Object.entries(this.TRANSLATION_KEYS)) {
      const translatedValue = await firstValueFrom(this.translate.get(translationKey));
      options.push({ key, value: translatedValue });
    }

    return options;
  }

  /**
   * Obtiene las opciones para el select de appliesTo (sin "Todo")
   */
  async getAppliesToOptions(): Promise<ConditionTypeOption[]> {
    const options: ConditionTypeOption[] = [];

    for (const [key, translationKey] of Object.entries(this.TRANSLATION_KEYS)) {
      const translatedValue = await firstValueFrom(this.translate.get(translationKey));
      options.push({ key, value: translatedValue });
    }

    return options;
  }

  /**
   * Obtiene el texto legible para un tipo de condición
   */
  getConditionTypeText(conditionType: string): string {
    const translationKey = this.TRANSLATION_KEYS[conditionType as keyof typeof this.TRANSLATION_KEYS];
    if (translationKey) {
      return this.translate.instant(translationKey);
    }
    return conditionType;
  }

  /**
   * Obtiene todos los valores de appliesTo
   */
  getAllAppliesToValues(): string[] {
    return Object.values(this.APPLIES_TO_VALUES);
  }

  /**
   * Verifica si un valor es válido para appliesTo
   */
  isValidAppliesToValue(value: string): boolean {
    return Object.values(this.APPLIES_TO_VALUES).includes(value as any);
  }


  /**
   * Obtiene las opciones para el select de unit (tipo de descuento) para partners
   */
  async getUnitOptions(): Promise<ConditionTypeOption[]> {
    const options: ConditionTypeOption[] = [];
    
    const percentageText = await firstValueFrom(this.translate.get('LISTS.PARTNER.UNIT_PERCENTAGE'));
    const fixedText = await firstValueFrom(this.translate.get('LISTS.PARTNER.UNIT_FIXED'));
    
    options.push({ key: '%', value: percentageText });
    options.push({ key: 'fixed', value: fixedText });
    
    return options;
  }

  /**
   * Obtiene la estructura de datos por defecto para condiciones de partner
   */
  getDefaultPartnerStructure(): any {
    return {
      id: { S: "" },
      partnerCode: { S: "" }, // Se llenará desde el parámetro de la URL
      appliesTo: { S: this.APPLIES_TO_VALUES.CALLS }, // Valor por defecto
      revenue: {
        L: [{
          M: {
            max: { N: "99999" },
            min: { N: "0" },
            quantity: { N: "1.5" },
            unit: { S: "%" }
          }
        }]
      }
    };
  }
}
